import React from "react";
import { DateTime } from "luxon";
import { ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import {
  footprintDeEvento,
  footprintDesdeResumen,
  hayConflictoEntreFootprints,
} from "../../utils/espacios";

const EVENT_TYPE_LABELS = {
  scouting: "Scouting",
  event: "Evento",
  reunion: "Reunión",
  bloqueo: "Bloqueo",
};

const toDateTime = (value) => {
  if (DateTime.isDateTime(value)) return value;
  if (value instanceof Date) return DateTime.fromJSDate(value);
  if (typeof value === "string") return DateTime.fromISO(value);
  return null;
};

// Motivo humano del conflicto entre footprints (null si el conflicto es
// puramente temporal — venue sin espacios o eventos simultáneos apagados).
const motivoConflicto = (miFp, suFp, espaciosPorId) => {
  const nombreEspacio = (id) => espaciosPorId[id]?.nombre || id;
  if (!miFp || !suFp) return "ocupa todo el venue";
  const interseccion = [...miFp].filter((u) => suFp.has(u));
  if (interseccion.length > 0)
    return `por ${interseccion.map(nombreEspacio).join(", ")}`;
  for (const a of miFp) {
    for (const b of (espaciosPorId[a]?.incompatibleCon || [])) {
      if (suFp.has(b))
        return `${nombreEspacio(a)} es incompatible con ${nombreEspacio(b)}`;
    }
  }
  return null;
};

// Dos etapas: cruce temporal y luego footprint — contención +
// incompatibilidad. Venue sin espacios o con eventos simultáneos apagados ⇒
// todo cruce temporal es conflicto. `excludeId` deja fuera al propio evento
// cuando se valida una edición. Devuelve [{ev, motivo}].
export const getClashingEvents = ({
  start,
  end,
  espaciosIds,
  excludeId = null,
  existingEvents = [],
  espaciosPorId = {},
  permiteEventosSimultaneos = true,
}) => {
  const inicio = toDateTime(start);
  const fin = toDateTime(end);
  if (!inicio?.isValid || !fin?.isValid) return [];
  const tieneEspacios = Object.keys(espaciosPorId).length > 0;
  const miFootprint = tieneEspacios
    ? footprintDesdeResumen(espaciosIds, espaciosPorId)
    : null;
  const result = [];
  existingEvents.forEach((ev) => {
    if (excludeId && ev.id === excludeId) return;
    if (ev.extendedProps?.status === "cancelado") return;
    const evStart = toDateTime(ev.start);
    const evEnd = toDateTime(ev.end);
    if (!evStart?.isValid || !evEnd?.isValid) return;
    if (!(inicio < evEnd && fin > evStart)) return;
    if (tieneEspacios && permiteEventosSimultaneos) {
      const suFootprint = footprintDeEvento(ev);
      if (!hayConflictoEntreFootprints(miFootprint, suFootprint, espaciosPorId))
        return;
      result.push({
        ev,
        motivo: motivoConflicto(miFootprint, suFootprint, espaciosPorId),
      });
      return;
    }
    result.push({ ev, motivo: null });
  });
  return result;
};

// `events` son {ev, motivo}: motivo nombra el espacio que causa el cruce
// (o null cuando el venue no maneja espacios).
export const ClashWarning = ({ events, titulo = "Las fechas se cruzan con:" }) => (
  <div className="mt-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 px-3 py-2.5 text-sm text-red-700 dark:text-red-400">
    <div className="flex items-center gap-2">
      <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
      <span className="font-medium">{titulo}</span>
    </div>
    <ul className="mt-1.5 ml-6 list-disc space-y-0.5">
      {events.map(({ ev, motivo }) => {
        const evType = ev.extendedProps?.eventType || ev.eventType;
        const typeLabel = EVENT_TYPE_LABELS[evType];
        return (
          <li key={ev.id}>
            {typeLabel && <span className="font-semibold">{typeLabel}:</span>}{" "}
            {ev.title || ev.nombre || "Sin nombre"}
            {motivo && <span className="text-red-500/80"> — {motivo}</span>}
          </li>
        );
      })}
    </ul>
  </div>
);
